import {PaginationResponsiveConfig, SupportedWidth} from '../types';

type SupportedWidthIncZero = SupportedWidth | 0;

type PageData = {
  pageNumber: number;
  isEllipsis: boolean;
  minWidth: SupportedWidthIncZero;
  maxWidth?: SupportedWidth;
};

type Breakpoint = {
  buttonsLimit: number;
  maxTotalPagesToRenderAllPages: number;
  minWidth: SupportedWidthIncZero;
  maxWidth?: SupportedWidth;
};

type ComputePropsForBreakpoint = {
  currentPage: number;
  totalPages: number;
  breakpoint: Breakpoint;
};

/**
 * собираем конфиги по всем брейкпойнтам,
 * недозаполненные заполняются по предыдущему брейкпойнту
 */
const convertConfigToBreakpoints = (
  config: PaginationResponsiveConfig,
): Array<Breakpoint> => {
  const sortedBreakpoints = [...(config.breakpoints || [])]
    // должно быть указано хотя бы одно из двух полей
    .filter((item) => item.maxTotalPagesToRenderAllPages || item.buttonsLimit)
    .sort((l, r) => l.minWidth - r.minWidth);

  const result: Array<Breakpoint> = [
    {
      minWidth: 0,
      maxWidth: sortedBreakpoints[0]?.minWidth,
      buttonsLimit: config.buttonsLimit,
      maxTotalPagesToRenderAllPages: config.maxTotalPagesToRenderAllPages,
    },
  ];

  for (let i = 0; i < sortedBreakpoints.length; i++) {
    const prevConfig = result[i];
    const currentConfig = sortedBreakpoints[i];
    const nextConfig = sortedBreakpoints[i + 1];

    result.push({
      ...prevConfig,
      ...currentConfig,
      maxWidth: nextConfig?.minWidth || undefined,
    });
  }

  return result;
};

/**
 * вычисляет окно индексов страниц вокруг текущей, которые будут отрисованы
 * startPage всегда больше чем 1, endPage всегда меньше чем totalPages
 */
const computeWindowAroundCurrentPage = ({
  currentPage,
  totalPages,
  breakpoint: {buttonsLimit},
}: ComputePropsForBreakpoint): {startPage: number; endPage: number} => {
  const freeSlots = buttonsLimit - 2; // первая и последняя всегда будут нарисованы
  const lookaround = Math.floor(freeSlots / 2);
  if (currentPage < freeSlots - lookaround + 1) {
    // где-то в начале - рисуем (p p p p p ... p)
    return {startPage: 2, endPage: freeSlots + 1};
  }
  if (totalPages - currentPage < freeSlots - 1) {
    // где-то в конце - рисуем (p ... p p p p p)
    return {startPage: totalPages - freeSlots, endPage: totalPages - 1};
  }

  // (p ... p p p p p .. p)
  let startPage = currentPage - lookaround;
  const endPage = currentPage + lookaround;
  if (endPage - startPage >= freeSlots) {
    startPage++;
  }
  return {startPage, endPage};
};

/**
 * вычисляет массив страниц на отрисовку при заданных лимитах
 */
export const computePagesForBreakpoint = (
  props: ComputePropsForBreakpoint,
): Array<PageData> => {
  const {
    totalPages,
    breakpoint: {
      buttonsLimit,
      maxTotalPagesToRenderAllPages,
      minWidth,
      maxWidth,
    },
  } = props;

  if (totalPages <= Math.max(buttonsLimit, maxTotalPagesToRenderAllPages)) {
    return Array.from({length: totalPages}, (_, index) => ({
      pageNumber: index + 1,
      isEllipsis: false,
      minWidth,
      maxWidth,
    }));
  }

  const {startPage, endPage} = computeWindowAroundCurrentPage(props);

  const startPageIsEllipsis = startPage > 2;
  const endPageIsEllipsis = endPage < totalPages - 1;

  const positions: PageData[] = [
    {pageNumber: 1, isEllipsis: false, minWidth, maxWidth},
  ];

  for (let i = startPage; i <= endPage; i++) {
    const isEllipsis =
      (i === startPage && startPageIsEllipsis) ||
      (i === endPage && endPageIsEllipsis);
    positions.push({pageNumber: i, isEllipsis, minWidth, maxWidth});
  }

  positions.push({
    pageNumber: totalPages,
    isEllipsis: false,
    minWidth,
    maxWidth,
  });

  if (
    positions.length !== props.breakpoint.buttonsLimit &&
    process.env.NODE_ENV === 'development'
  ) {
    console.error(
      `Invalid pagination computation: got length ${positions.length} with config=${JSON.stringify(props, undefined, 4)} (${endPage - startPage})`,
    );
  }
  return positions;
};

/**
 * Склеиваем странички которые совпадают у соседних брейкпойнтов
 * (чтобы рендерить поменьше элементов)
 */
const mergeSamePagesAcrossBreakpoints = (pages: PageData[]): PageData[] =>
  pages
    .reduce((a, page) => {
      const matchingPage = a.find(
        (item) =>
          item.pageNumber === page.pageNumber &&
          item.isEllipsis === page.isEllipsis &&
          item.maxWidth === page.minWidth,
      );
      if (matchingPage) {
        matchingPage.maxWidth = page.maxWidth;
      } else {
        a.push({...page});
      }
      return a;
    }, [] as PageData[])
    .sort((l, r) => l.pageNumber - r.pageNumber);

export default function computePagesToRender({
  currentPage,
  totalPages,
  responsiveConfig,
}: {
  currentPage: number;
  totalPages: number;
  responsiveConfig: PaginationResponsiveConfig;
}) {
  const breakpoints = convertConfigToBreakpoints(responsiveConfig);
  const pagesForAllBreakpoints: Array<PageData> = breakpoints
    .map((breakpoint) =>
      computePagesForBreakpoint({currentPage, totalPages, breakpoint}),
    )
    .flat();

  return mergeSamePagesAcrossBreakpoints(pagesForAllBreakpoints);
}
